import { ReactNode } from 'react';
import { ImageProps } from 'next/image';

export interface ApiPostData<T> {
  data: T;
}

export interface ApiResponse<T> {
  data: T;
  meta: ApiMetaReponse;
}
export interface NonStandardApiResponse<T> {
  data: T;
  meta: any;
}

interface ApiMetaReponse {
  pagination?: ApiPaginationResponse;
}

interface ApiPaginationResponse {
  page: number;
  pageSize: number;
  pageCount: number;
  total: number;
}

export interface EventLeadCapture {
  firstName: string;
  lastName: string;
  phone: string;
  guestFirstName: string;
  guestLastName: string;
  guestPhone: string;
  email: string;
  guestEmail: string | null;
  event: string;
  exhibition: string;
}

export interface Artist {
  id: number;
  name: string;
  description: string | null;
  city: string | null;
  state: string | null;
  country: string | null;
  firstName: string;
  lastName: string;
  website: string | null;
  facebook: string | null;
  instagram: string | null;
  twitter: string | null;
  birthYear: string | null;
  displayForPreferences: boolean | null;
  acceptingCommissions: boolean | null;
  quote: string | null;
  subQuote: string | null;
  videoUrl: string | null;
  published_at: Date;
  created_at: Date;
  updated_at: Date;
  url_handle: string;
  publicEmail: string | null;
  contactEmail: string | null;
  urlHandler: string | null;
  isCollectible: string | null;
  links: Link[];
  image: CloudinaryImage;
  products: any[];
  similarProducts: any[];
  variants: Variant[];
  profile_header: ArtistProfileHeader;
  related_artists: Artist[];
}

export interface ArtistProfileHeader {
  id: number;
  title: string;
  button_copy: string;
  button_link_url: string;
  image: CloudinaryImage;
}

export interface Show {
  id: number;
  name: string;
  release_date: Date;
  variants: Variant[];
}

export interface CountResponse {
  count: number;
}

export interface SumResponse {
  sum: number;
}

export interface CollectorCountVariant extends Variant {
  count: number;
}

export interface CollectorCountArtist extends Artist {
  count: number;
}

export interface Variant {
  id: number;
  name: string;
  releaseDate: Date | string;
  medium: string | null;
  dimensions: string | null;
  editionNumber: string | null;
  publishedBy: string | null;
  releasePrice: number;
  marketValue: number;
  shopifyID: string | null;
  featured: boolean | null;
  collectible: boolean;
  cmDimensions: string | null;
  inDimensions: string | null;
  artist: Artist;
  description: string | null;
  published_at: Date;
  created_at: Date;
  updated_at: Date;
  image: CloudinaryImage[];
  market_place_listings?: MarketPlaceListing[];
}

export interface VariantWithShopify extends Variant {
  shopify: ShopifyProduct;
}

export interface WaitList {
  artist: Artist;
  id: number;
  author: User;
  prints: boolean | null;
  collectibles: boolean | null;
  stickers: boolean | null;
  apparel: boolean | null;
  nft: boolean | null;
  paintings: boolean | null;
  sculptures: boolean | null;
  painting_budget: number;
  sculpture_budget: number;
  createdAt: string;
  gallery?: Gallery;
}

export interface Gallery {
  id: number;
  name: string;
  published_at: Date;
  created_at: Date;
  updated_at: Date;
  referralCode: string;
  links: Link[];
  image: CloudinaryImage;
}
export interface Link {
  id: number;
  title: string;
  value: string;
}

export interface User {
  id: number;
  username: string;
  email: string;
  provider: string;
  confirmed: boolean;
  blocked: boolean | null;
  role: UserRole;
  phoneNumber: string | null;
  streetAddress1: string | null;
  streetAddress2: string | null;
  city: string | null;
  state: string | null;
  zipCode: string | null;
  birthDay: Date | null;
  firstName: string;
  lastName: string;
  country: string | null;
  piecesOfArtOwned: number | null;
  collectingExperience: null;
  isArtist: boolean | null;
  verified: string | null;
  created_at: Date;
  updated_at: Date;
  discover_profile: number;
  url_handle: string;
  is_profile_sharing_enabled: boolean;
  collection_piece_count?: number;
}

export interface UserRole {
  id: number;
  name: string;
  description: string;
  type: string;
}

export interface CloudinaryImage {
  id: number;
  name: string;
  alternativeText: string | null;
  caption: string | null;
  width: number;
  height: number;
  formats: CloudinaryImageFormats;
  hash: string | null;
  ext: string | null;
  mime: string | null;
  size: number;
  url: string;
  previewUrl: string | null;
  provider: string | null;
  provider_metadata: ProviderMetadata;
  created_at: Date;
  updated_at: Date;
}

export interface CloudinaryImageFormats {
  large?: CloudinaryImageFormat;
  small?: CloudinaryImageFormat;
  medium?: CloudinaryImageFormat;
  thumbnail?: CloudinaryImageFormat;
}

export interface CloudinaryImageFormat {
  ext: string | null;
  url: string;
  hash: string | null;
  mime: string | null;
  name: string | null;
  path: string | null;
  size: number;
  width: number;
  height: number;
  provider_metadata: ProviderMetadata;
}

export interface ProviderMetadata {
  public_id: string | null;
  resource_type: string | null;
}

export interface BaseWaitListSettings {
  id: number;
  prints: boolean;
  published_at?: Date;
  created_at?: Date;
  updated_at?: Date;
  collectibles: boolean;
  apparel: boolean;
  stickers: boolean;
  paintings: boolean;
  sculptures: boolean;
  painting_budget_min: number;
  painting_budget_max: number;
  sculpture_budget_min: number;
  sculpture_budget_max: number;
  average_print_budget: number;
  average_apparel_budget: number;
  average_collectible_budget: number;
  average_sticker_budget: number;
  budget_step?: number | null;
  painting_budget_step: number;
  sculpture_budget_step: number;
  nft: boolean;
}
export interface ArtistWaitListSettings extends BaseWaitListSettings {
  artist: Artist;
}

export interface CollectionPiece {
  id: number;
  variant: Variant;
  purchaseDate: Date;
  price: number;
  description: string | null;
  user: User;
  editionNumber: string | null;
  artist: Artist | null;
  purchasedFrom: string | null;
  author: User;
  typeOfSale: string | null;
  published_at: Date;
  created_at: Date;
  updated_at: Date;
  media: CloudinaryImage[];
}

export type CtaButtonType = 'button' | 'submit' | 'reset' | undefined;

export interface StringStringMap {
  [key: string]: string;
}
export interface Faq {
  question: string;
  answer: string | ReactNode;
}

export interface RightCheveronProps {
  stroke?: string;
  large?: boolean;
  hover?: string;
  transition?: string;
}

export interface TwelveColGridProps {
  mx?: string;
  my?: string;
  px?: string;
  py?: string;
  gap?: string;
  width?: string;
  addClassStyles?: string;
  children?: React.ReactNode;
}

export interface WatchlistVariantsApiResponse {
  variant: number;
  artist: number;
}

export interface FillImageProps {
  height: string;
  width: string;
  src: string;
  alt?: string;
  priority?: boolean;
  objectFit?: ImageProps['objectFit'];
}

export interface ArtisWaitlistSums {
  painting_budget_sum: number;
  paintings_count: number;
  sculpture_budget_sum: number;
  sculptures_count: number;
  prints_count: number;
  stickers_count: number;
  apparel_count: number;
  collectibles_count: number;
  nft_count: number;
}

export interface StringMap {
  [key: string]: any;
}

export interface SortAndFilterOption {
  title: string;
  query: StringMap;
}

export interface FilterOptionV2 {
  title: string;
  filters: StringMap;
}
export interface PopulationOptionV2 {
  populate: StringMap;
}
export interface SortOptionV2 {
  title: string;
  sort: string;
}

export interface UserAndJwt {
  jwt: string;
  user: User;
}

export interface MultiSearchResponse {
  variants: ResultResponse<Variant[]>;
  artists: ResultResponse<Artist[]>;
  shows: ResultResponse<Show[]>;
}

interface ResultResponse<T> {
  results: T;
  meta: ApiMetaReponse;
}

export interface ShopifyProduct {
  id: number;
  title: string;
  body_html: string;
  vendor: string;
  product_type: string;
  created_at: Date;
  handle: string;
  updated_at: Date;
  published_at: Date;
  template_suffix: string;
  status: string;
  published_scope: string;
  tags: string;
  admin_graphql_api_id: string;
  variants: ShopifyVariant[];
  options: ShopifyOption[];
  images: ShopifyImage[];
  image: ShopifyImage;
}

export interface ShopifyImage {
  id: number;
  product_id: number;
  position: number;
  created_at: Date;
  updated_at: Date;
  alt: null;
  width: number;
  height: number;
  src: string;
  variant_ids: any[];
  admin_graphql_api_id: string;
}

export interface ShopifyOption {
  id: number;
  product_id: number;
  name: string;
  position: number;
  values: string[];
}

export interface ShopifyVariant {
  id: number;
  product_id: number;
  title: string;
  price: string;
  sku: string;
  position: number;
  inventory_policy: string;
  compare_at_price: null;
  fulfillment_service: string;
  inventory_management: string;
  option1: string;
  option2: null;
  option3: null;
  created_at: Date;
  updated_at: Date;
  taxable: boolean;
  barcode: string;
  grams: number;
  image_id: null;
  weight: number;
  weight_unit: string;
  inventory_item_id: number;
  inventory_quantity: number;
  old_inventory_quantity: number;
  requires_shipping: boolean;
  admin_graphql_api_id: string;
}

export interface DiscoveryInteractionReference {
  liked: boolean;
  author: number;
  variant: number;
}

export interface DiscoveryInteraction {
  liked: boolean;
  author: User;
  variant: Variant;
}

export enum DiscoveryInteractionSwipeValues {
  Dislike = -1,
  None = 0,
  Like = 1,
}

export enum VerificationLevels {
  UNVERIFIED = 'UNVERIFIED',
  PENDING = 'PENDING',
  V1 = 'V1',
  V2 = 'V2',
  V3 = 'V3',
}

export interface ArtistInfo {
  id: number;
  name: string;
}
export interface VariantRequestArtistInfo {
  artist_name: string;
}

export interface DistinctArtistsInCollectionResponse {
  collectionPieceArtists: ArtistInfo[];
  variantRequestArtists: VariantRequestArtistInfo[];
}

export interface CpAndVRForOneArtistResponse {
  collectionPieces: CollectionPiece[];
  variantRequests: VariantRequest[];
}

export interface VariantAndVRForOneArtistResponse {
  collectionPieceVariants: Variant[];
  variantRequests: VariantRequest[];
}

export interface CollectionPieceFormValues {
  variant: number;
  author: number;
  price: number;
  purchaseDate: string | null;
  editionNumber: string;
  purchasedFrom: string;
  description: string;
  typeOfSale: string;
  media: never[];
}

export interface ArtistWaitlistFormValues {
  prints: boolean | null;
  collectibles: boolean | null;
  stickers: boolean | null;
  sculptures: boolean | null;
  sculpture_budget: number;
  painting_budget: number;
  paintings: boolean | null;
  nft: boolean | null;
  apparel: boolean | null;
  artist: number;
  gallery?: string | null;
  author: number;
}
export interface ArtistApplicationFormValues {
  name: string;
  firstName: string;
  lastName: string;
  currentCity: string;
  email: string;
  phoneNumber: string;
  instagram: string;
  website: string;
  comments: string;
}

export enum DiscoveryAlgoVersion {
  V1,
  V2,
  V3,
}

export interface VariantRequest {
  id: number;
  name: string;
  price: number;
  marketValue: number;
  purchaseDate: string | null;
  description: string | null;
  editionNumber: string | null;
  typeOfSale: string | null;
  purchasedFrom: string | null;
  author: User;
  published_at: Date;
  created_at: Date;
  updated_at: Date;
  artistName: string;
  releasePrice: number | null;
  image: CloudinaryImage;
  media: CloudinaryImage[];
}

export interface UserProfileVerfication {
  id: number;
  is_profile_sharing_enabled: boolean;
  url_handle: string;
}

export interface CollectionTotal {
  total_purchase_price: number;
  total_market_value: number;
}

export interface StripeUrl {
  url: string;
}

export interface UserMarketPlace {
  id: number;
  stripeConnectAccountId: string;
  createdAt: Date;
  updatedAt: Date;
  publishedAt: Date;
  status: string;
  author?: User;
}

export interface MarketPlaceListing {
  id: number;
  status: string;
  variant: Variant;
  user_market_place: UserMarketPlace;
  stripeProductId: string;
  price: number;
  shippingRate: number;
  description: string | null;
  editionNumber: string;
  media?: CloudinaryImage[];
  url_handle?: string;
}

export interface MarketPlaceOrderShippingAddress {
  id: number;
  name: string;
  city: string;
  country: string;
  line1: string;
  line2: string | null;
  postalCode: string;
  state: string;
}

export interface MarketPlaceOrder {
  id: number;
  updatedAt: Date;
  createdAt: Date;
  publishedAt: Date;
  status: string;
  stripePaymentId: string;
  carrier: string | null;
  trackingId: string | null;
  market_place_listing: MarketPlaceListing;
  shippingAddress: MarketPlaceOrderShippingAddress;
}

export interface MarketPlaceOrderShippingDetailFormValues {
  carrier: string;
  trackingId: string;
}

export interface CreateMarketPlaceListingFormValues {
  variant: number;
  price: number;
  shippingRate: number;
  description: string;
  editionNumber: string;
  media: File[] | CloudinaryImage[];
}

export interface BaseStyleProps {
  my?: string;
  mx?: string;
  py?: string;
  px?: string;
  gap?: string;
  height?: string;
  width?: string;
  border?: string;
  background?: string;
  position?: string;
  top?: string;
  bottom?: string;
  left?: string;
  right?: string;
  colSpan?: string;
  rowSpan?: string;
  addClassStyles?: string;
  color?: string;
  fontSize?: string;
  fontWeight?: string;
  display?: string;
  fill?: string;
  stroke?: string;
}

export enum MarketPlaceListingStatus {
  REVIEW = 'REVIEW',
  AVAILABLE = 'AVAILABLE',
  SOLD = 'SOLD',
  SHIPPED = 'SHIPPED',
}

export enum MarketPlaceOrderStatus {
  CONFIRMED = 'CONFIRMED',
  SHIPPED = 'SHIPPED',
}

export enum MarketPlaceOrderCarriers {
  CUSTOM = 'ENTER CUSTOM CARRIER...',
  FEDEX = 'FEDEX',
  UPS = 'UPS',
  USPS = 'USPS',
  DHL = 'DHL',
}

export enum EventFormRegistrationSteps {
  REGISTER = 'REGISTER',
  CONFIRM = 'CONFIRM',
}

export interface SignupAndArtistWaitlistFormValues
  extends SignupFormValues,
    ArtistWaitlistFormValues {}

export interface SignupFormValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  passwordConfirmation: string;
  phoneNumber: string;
  city: string;
  tcAgree: boolean;
  updatesAgree: boolean;
  smsOptedIn: boolean;
}
